import { Spinner, toast } from '@gmg/gmg-react-components';
import { FC, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AppContext from 'src/AppContext';
import { useEditFolder } from 'src/graphql/useEditFolder';
import { useCreateFolder } from '../../graphql/useCreateFolder';
import { useGetFolderDetails } from '../../graphql/useGetFolderDetails';
import { FolderListItem } from '../../viewModels/Folder';
import { getErrorMsgForToast } from '../toastErrorHandler';
import FolderModal from './FolderModal';

export interface FolderModalContainerProps {
    onClose?: () => void;
    siblingFolderNames: Array<string>;
    parentFolder?: FolderListItem; // this is undefined in "edit" use case or if a root folder is being created
    folderToEdit?: FolderListItem; // this is undefined in "create" use case
    onSubmit: (folder: FolderListItem) => void;
    orgId: string;
    groups: Map<string, string>;
}

const FolderModalContainer: FC<FolderModalContainerProps> = props => {

    const useCase: 'edit' | 'add' = props.folderToEdit ? 'edit' : 'add';

    const { t } = useTranslation();
    const onClose = props.onClose!;
    const { onError } = useContext(AppContext);
    const createFolderMutation = useCreateFolder();
    const editFolderMutation = useEditFolder();

    const folderToFetch = useCase === 'edit'
        // Fetch inherited and directly assigned groups of folder.
        ? props.folderToEdit?.id
        // A new folder has no groups. Fetch inherited groups from parent folder.
        // Will be undefined when creating a root folder: No folders need to be fetched.
        : props.parentFolder?.id;

    const { data: groups, isFetching, error } = useGetFolderDetails(folderToFetch);

    useEffect(() => {
        if (error) {
            onClose();
            onError(error as Error);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error, onError]);

    const getInheritedGroupsForAddFolder = (groupType: 'editorGroups' | 'readerGroups') =>
        !props.parentFolder
            // create a root folder
            ? []
            // create a subfolder: all groups are from the parent folder, i.e. are inherited. A new folder has no groups directly assigned.
            : groups
                ? groups[groupType].map(g => g.groupId)
                : []
        ;

    const handleSubmit = (
        trimmedFolderName: string,
        groupsEditingRights: Array<string>,
        groupsViewingRights: Array<string>,
    ) => {

        props.onClose?.();

        const wrapperPromise = new Promise<void>(async (resolve, reject) => {
            const parentFlderId = useCase === 'edit'
                ? props.folderToEdit?.parentFolderId
                : props.parentFolder?.id;

            const requestPayload: any = {
                orgId: props.orgId,
                folderName: trimmedFolderName,
                parentFolderId: parentFlderId,
                editorGroups: groupsEditingRights,
                readerGroups: groupsViewingRights,
            };
            if (useCase === 'edit') {
                requestPayload.folderId = props.folderToEdit!.id;
            }
            const mutation = useCase === 'edit'
                ? editFolderMutation
                : createFolderMutation;

            const result = await mutation.mutateAsync(requestPayload);

            const updatedOrNewFolder: FolderListItem = useCase === 'edit'
                ? {
                    ...props.folderToEdit!,
                    name: trimmedFolderName,
                } : {
                    parentFolderId: props.parentFolder?.id,
                    id: result,
                    name: trimmedFolderName,
                    isExpanded: false,
                    level: props.parentFolder
                        ? props.parentFolder.level + 1
                        : 0,
                    isTraversable: true,
                };

            props.onSubmit(updatedOrNewFolder);

            resolve();
        });

        void toast.promise(wrapperPromise, {
            loading: t('Common.Toast.Loading', 'Loading'),
            success: useCase === 'edit'
                ? t('FolderModalContainer.toast_edit_success_desc', {
                    defaultValue: 'You have successfully updated "{{folderName}}"!',
                    folderName: trimmedFolderName,
                })
                : t('FolderModalContainer.toast_success_desc', {
                    defaultValue: 'You have successfully added "{{folderName}}"!',
                    folderName: trimmedFolderName,
                }),
            error: response => getErrorMsgForToast(response, t),
        });
    };

    const folderModalCommonProps = {
        onSubmit: handleSubmit,
        onClose,
        siblingFolderNames: props.siblingFolderNames,
        availableGroups: props.groups,
    };

    if (isFetching) {
        return <Spinner />;
    }

    return (
        useCase === 'edit'
            ? <FolderModal
                {...folderModalCommonProps}
                folderToEdit={props.folderToEdit}
                initialFolderName={props.folderToEdit!.name}
                isRoot={!props.folderToEdit!.parentFolderId}
                assignedGroups={
                    {
                        editorGroups: groups ? groups!.editorGroups.filter(g => g.inherited === false).map(g => g.groupId) : [],
                        readerGroups: groups ? groups!.readerGroups.filter(g => g.inherited === false).map(g => g.groupId) : [],
                    }
                }
                inheritedGroups={
                    {
                        editorGroups: groups ? groups!.editorGroups.filter(g => g.inherited === true).map(g => g.groupId) : [],
                        readerGroups: groups ? groups!.readerGroups.filter(g => g.inherited === true).map(g => g.groupId) : [],
                    }
                }
            />
            : <FolderModal
                {...folderModalCommonProps}
                initialFolderName={''}
                isRoot={!props.parentFolder}
                assignedGroups={
                    {
                        editorGroups: [],
                        readerGroups: [],
                    }
                }
                inheritedGroups={
                    {
                        editorGroups: getInheritedGroupsForAddFolder('editorGroups'),
                        readerGroups: getInheritedGroupsForAddFolder('readerGroups'),
                    }
                }
            />
    );
};

export default FolderModalContainer;
